import { useTranslation } from 'react-i18next';

import type { Workspace } from '@/entities/workspace';
import { CreateMenu as SharedCreateMenu, type CreateItem } from '@/features/create-menu';
import { Icons } from '@/shared/ui';

interface Props {
  /** The personal workspace (boards and tasks can always be created there). */
  personal: Workspace | undefined;
  /** Whether any workspace lets the viewer create tasks: one "New task" opens the workspace choice. */
  canCreateTask: boolean;
  onNewTask: () => void;
  onNewBoard: () => void;
  /** Offered when some workspace allows the viewer to create databases. */
  onNewDatabase?: () => void;
  /** Offered when some workspace allows the viewer to create notes (the dialog asks which). */
  onNewDocument?: () => void;
  /** Offered when something can be imported: ONE entry, the chooser asks what. */
  onImport?: () => void;
  onNewWorkspace: () => void;
}

/** The home "Create" menu: creation, and ONE "Import" entry that asks what to import. */
export function CreateMenu({
  personal,
  canCreateTask,
  onNewTask,
  onNewBoard,
  onNewDatabase,
  onNewDocument,
  onImport,
  onNewWorkspace,
}: Props) {
  const { t } = useTranslation();
  const items: CreateItem[] = [];
  if (canCreateTask) {
    items.push({
      id: 'task',
      label: t('home.newTask'),
      icon: <Icons.check />,
      onSelect: onNewTask,
    });
  }
  if (personal) {
    items.push({
      id: 'board',
      label: t('workspace.newBoard'),
      icon: <Icons.board />,
      separated: true,
      onSelect: onNewBoard,
    });
  }
  if (onNewDatabase) {
    items.push({
      id: 'database',
      label: t('databases.new'),
      icon: <Icons.layers />,
      onSelect: onNewDatabase,
    });
  }
  if (onNewDocument) {
    items.push({
      id: 'document',
      label: t('documents.new'),
      icon: <Icons.document />,
      onSelect: onNewDocument,
    });
  }
  if (onImport) {
    items.push({
      id: 'import',
      label: t('import.menu'),
      icon: <Icons.import />,
      separated: true,
      onSelect: onImport,
    });
  }
  items.push({
    id: 'workspace',
    label: t('home.newWorkspace'),
    separated: true,
    icon: <Icons.workspace />,
    onSelect: onNewWorkspace,
  });
  return <SharedCreateMenu items={items} />;
}
